import { useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import type { CompositionEvent, FocusEvent, InputHTMLAttributes, KeyboardEvent, ReactNode, TextareaHTMLAttributes } from "react";
import { requestWritingReview, subscribeWritingSession, writingSessionToken, type WritingStatus } from "../api/writing-client";
import { suggestContextualPunctuation } from "./punctuation";
import { useAutocorrectedInput, useAutocorrectionPreference, WritingSuggestions } from "./use-autocorrection";
import "./writing.css";

interface WritingProps {
  label: ReactNode;
  value: string;
  onValueChange: (value: string) => void;
  kind?: "prose" | "title";
}
type TextareaProps = WritingProps & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange" | "children">;
type InputProps = WritingProps & Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "children" | "type">;
type Review = { original: string; status: WritingStatus | "reviewing" | "too-long"; proposal?: string };

export function WritingTextarea(props: TextareaProps) { return <WritingField {...props} multiline />; }
export function WritingInput(props: InputProps) { return <WritingField {...props} multiline={false} kind={props.kind ?? "title"} />; }

function WritingField({ label, value, onValueChange, kind = "prose", multiline, ...attributes }: (TextareaProps | InputProps) & { multiline: boolean }) {
  const generatedId = useId();
  const id = attributes.id ?? generatedId;
  const [enabled] = useAutocorrectionPreference();
  const token = useSyncExternalStore(subscribeWritingSession, writingSessionToken, () => "");
  const [literal, setLiteral] = useState(false);
  const [composing, setComposing] = useState(false);
  const [edited, setEdited] = useState<string | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [undoContext, setUndoContext] = useState<{ before: string; after: string } | null>(null);
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const active = enabled && !literal && !attributes.disabled && !attributes.readOnly;
  const language = (attributes.lang || document.documentElement.lang || navigator.language).split(/[-_]/)[0].toLowerCase();

  function cancel() { generation.current++; controller.current?.abort(); }
  function change(text: string) {
    cancel(); setReview(null); setUndoContext(null); setEdited(text); onValueChange(text);
  }
  const local = useAutocorrectedInput({ value, onValueChange: change, enabled: active, language,
    maxLength: attributes.maxLength, sentenceStructure: kind === "prose" });

  useEffect(() => {
    cancel(); setReview(null);
    if (!active || composing || edited !== value || !value.trim() || !["es", "en"].includes(language)) return;
    if (/[`{}=<>]/u.test(value)) return;
    if (new TextEncoder().encode(value).length > 4000) { setReview({ original: value, status: "too-long" }); return; }
    if (!token) { setReview({ original: value, status: "unavailable" }); return; }
    const current = generation.current;
    const abort = new AbortController(); controller.current = abort;
    const timer = window.setTimeout(async () => {
      setReview({ original: value, status: "reviewing" });
      try {
        const result = await requestWritingReview(value, language, kind, token, abort.signal);
        const proposal = result.status === "suggested" ? await suggestContextualPunctuation(value, language,
          async () => result.text!, { signal: abort.signal, maxLength: attributes.maxLength }) : null;
        if (!abort.signal.aborted && generation.current === current && writingSessionToken() === token)
          setReview({ original: value, status: result.status === "suggested" && !proposal ? "declined" : result.status, proposal: proposal?.text });
      } catch {
        if (!abort.signal.aborted && generation.current === current) setReview({ original: value, status: "unavailable" });
      }
    }, 1400);
    return () => { window.clearTimeout(timer); abort.abort(); };
  }, [value, edited, active, composing, language, kind, token, attributes.maxLength]);

  useEffect(() => { setUndoContext(null); setEdited(null); }, [token, language]);

  function applyContext() {
    if (!active || !review?.proposal || review.original !== value) return;
    cancel(); setEdited(null); setUndoContext({ before: value, after: review.proposal });
    onValueChange(review.proposal); setReview(null);
  }
  function undo() {
    cancel(); setEdited(null); setReview(null);
    if (undoContext?.after === value) { onValueChange(undoContext.before); setUndoContext(null); }
    else { local.undo(); setEdited(null); }
  }
  const props = {
    ...attributes, ...local.props, id, value,
    "aria-describedby": [attributes["aria-describedby"], `${id}-review`].filter(Boolean).join(" "),
    onBlur: (event: FocusEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      // An action must receive its click before a new review can change the form's layout.
      // Tab/click into another text field still completes the local review normally.
      // A field disabled by submitting loses focus while React still holds the pre-submit value;
      // reviewing it then would write the already-sent text back into the cleared draft.
      if (!event.currentTarget.disabled &&
          !(event.relatedTarget instanceof HTMLElement && event.relatedTarget.closest("button, input[type=submit]")))
        local.props.onBlur(event);
      attributes.onBlur?.(event);
    },
    onKeyDown: (event: KeyboardEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      if ((event.ctrlKey || event.metaKey) && !event.shiftKey && !event.altKey && event.key.toLowerCase() === "z" && undoContext?.after === value) {
        event.preventDefault(); undo();
      } else {
        local.props.onKeyDown(event);
        if (event.defaultPrevented) { cancel(); setEdited(null); setReview(null); }
      }
      attributes.onKeyDown?.(event);
    },
    onCompositionStart: (event: CompositionEvent<HTMLInputElement & HTMLTextAreaElement>) => {
      cancel(); setComposing(true); local.props.onCompositionStart(); attributes.onCompositionStart?.(event);
    },
    onCompositionEnd: (event: CompositionEvent<HTMLInputElement & HTMLTextAreaElement>) => { local.props.onCompositionEnd(event); setComposing(false); attributes.onCompositionEnd?.(event); }
  };
  const visible = active && review?.original === value ? review : null;
  const message = visible ? ({ reviewing: "ONE CORE está revisando…", suggested: "Puntuación propuesta por ONE CORE",
    unchanged: "ONE CORE no propone cambios.", declined: "No hay una propuesta segura. Se conserva tu texto.",
    unavailable: "Revisión contextual no disponible. Puedes continuar.", busy: "ONE CORE está ocupado. Puedes continuar.",
    "too-long": "Texto demasiado largo para la revisión contextual. Puedes continuar." } as const)[visible.status] : "";
  return <div className="writing-field">
    <label htmlFor={id}>{label}</label>
    {multiline ? <textarea {...props as TextareaHTMLAttributes<HTMLTextAreaElement>} ref={local.props.ref} /> : <input {...props as InputHTMLAttributes<HTMLInputElement>} type="text" ref={local.props.ref} />}
    <div className="writing-controls" id={`${id}-review`}>
      <button type="button" aria-pressed={literal} disabled={attributes.disabled || attributes.readOnly}
        onClick={() => { cancel(); setLiteral(!literal); setEdited(null); setReview(null); }}>
        {literal ? "Texto literal activado" : "Usar texto literal"}
      </button>
      <button type="button" disabled={!active || !(undoContext?.after === value || local.lastCorrection?.after === value)}
        style={{ visibility: undoContext?.after === value || local.lastCorrection?.after === value ? "visible" : "hidden" }}
        onClick={undo}>Deshacer corrección</button>
    </div>
    <details className="writing-details">
      <summary>Revisión de escritura <span aria-label={message || "Sin propuesta"}>
        {visible?.proposal || local.review?.suggestions.length ? "●" : "○"}
      </span></summary>
      <p role="status">{message || (active ? "Las propuestas aparecerán aquí al revisar el texto." : "Corrección desactivada para este texto.")}</p>
      {active && <WritingSuggestions review={local.review} onApply={suggestion => { local.applySuggestion(suggestion); setEdited(null); }} />}
      {visible?.proposal && <div className="writing-proposal">
        <p>{visible.proposal}</p><small>Comprueba que mantiene tu intención.</small>
        <button type="button" onClick={applyContext}>Aplicar puntuación</button>
      </div>}
    </details>
  </div>;
}

export function WritingSettings() {
  const [enabled, setEnabled] = useAutocorrectionPreference();
  return <label className="writing-setting"><input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
    <span>Corrección de escritura<small>Ortografía y revisión contextual con ONE CORE · en este dispositivo</small></span>
  </label>;
}
